
import * as React from "react";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectTrigger,
} from "@/components/ui/select";
import { Target } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";

import SortableItem from "./objectives/SortableItem";
import AddObjectiveForm from "./objectives/AddObjectiveForm";
import { useObjectives, Objective } from "@/hooks/use-objectives";

interface ObjectivesSelectProps {
  selectedObjectives: string[];
  setSelectedObjectives: (objectives: string[]) => void;
}

const ObjectivesSelect = ({ selectedObjectives, setSelectedObjectives }: ObjectivesSelectProps) => {
  const [open, setOpen] = React.useState(false);
  
  const {
    objectives,
    newObjective,
    setNewObjective,
    handleObjectiveToggle,
    handleDragEnd,
    handleAddObjective,
  } = useObjectives(selectedObjectives, setSelectedObjectives);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  React.useEffect(() => {
    console.log('Selected objectives updated:', selectedObjectives);
  }, [selectedObjectives]);

  const displayText = selectedObjectives.length > 0 
    ? `${selectedObjectives.length} Selected`
    : "Select objectives";

  const selectedItems = selectedObjectives.map(id => 
    objectives.find(obj => obj.id === id)!
  );
  const unselectedItems = objectives.filter(obj => 
    !selectedObjectives.includes(obj.id)
  );

  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor="objectives" className="text-sm font-medium">
        Objectives
      </Label>
      <Select open={open} onOpenChange={setOpen}>
        <SelectTrigger 
          id="objectives"
          className="w-full bg-white"
          onClick={(e) => {
            e.preventDefault();
            setOpen(true);
          }}
        >
          <div className="flex items-center gap-2">
            <Target className="w-4 h-4 text-[#FF4A8E]" />
            <span className="text-sm">{displayText}</span>
          </div>
        </SelectTrigger>
        <SelectContent className="w-[280px]">
          <SelectGroup>
            <AddObjectiveForm 
              value={newObjective}
              onChange={setNewObjective}
              onAdd={handleAddObjective}
            />

            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={selectedItems.map(obj => obj.id)}
                strategy={verticalListSortingStrategy}
              >
                {selectedItems.map((objective, index) => (
                  <SortableItem
                    key={objective.id}
                    id={objective.id}
                    label={objective.label}
                    index={index}
                    isCustom={objective.isCustom}
                    checked={true}
                    onCheckedChange={() => handleObjectiveToggle(objective.id)}
                  />
                ))}
              </SortableContext>
            </DndContext>

            {selectedItems.length > 0 && unselectedItems.length > 0 && (
              <div className="my-1 border-t border-gray-200" />
            )}

            {unselectedItems.map((objective) => (
              <div 
                key={objective.id}
                className={`flex items-center gap-2 px-2 py-1.5 cursor-pointer hover:bg-accent hover:text-accent-foreground ${objective.isCustom ? "italic" : ""}`}
              >
                <Checkbox
                  id={objective.id}
                  checked={false}
                  onCheckedChange={() => handleObjectiveToggle(objective.id)}
                />
                <label
                  htmlFor={objective.id}
                  className="text-sm font-medium cursor-pointer flex-1"
                >
                  {objective.label}
                </label>
              </div>
            ))}
          </SelectGroup>
          {selectedObjectives.length >= 5 && (
            <div className="px-2 py-1 text-xs text-gray-500">
              Maximum 5 objectives selected
            </div>
          )}
        </SelectContent>
      </Select>
    </div>
  );
};

export default ObjectivesSelect;
